import { useCallback, useContext, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { CompanyUsersSelect } from 'src/components/domain/users/components/company-users-select';
import { useSetFormFieldValue } from 'src/components/filters/helpers';
import { LggDropdownButtonWithoutOverlay } from 'src/components/general/button/dropdown-button';
import { LggPopover } from 'src/components/general/display/popover';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import { Icon } from 'src/components/general/icon';
import {
  OnlyMeAvatar,
  UnassignedPrefixIcon,
  StyledStackedCompanyUserList,
} from 'src/components/general/inputs/assignee-input-components';
import { preventDefaultAndStopPropagation } from 'src/components/general/inputs/helpers';
import { Radio } from 'src/components/general/inputs/radio';
import { OverlayFilterActionButtons } from 'src/components/general/overlay/overlay-filter-action-buttons';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { DashboardFiltersContext } from 'src/components/pages/dashboard/components/filters/dashboard-filters-context';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentUser } from 'src/hooks/use-current-user';
import { useVisible } from 'src/hooks/use-visible';

const MobileDropdownButton = styled(LggDropdownButtonWithoutOverlay)<{
  $isActive: boolean;
}>`
  height: 32px;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.koala};
  font-size: 13px;

  ${({ $isActive }) =>
    $isActive &&
    css`
      &,
      &:hover,
      &:focus {
        background: ${({ theme }) => theme.colors.secondaryTopaz10};
        border: 1px solid ${({ theme }) => theme.colors.secondaryTopaz40};
      }
    `};
`;

const UserFilterDropdown = styled(LggDropdownButtonWithoutOverlay)`
  height: 32px;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.koala};
  font-size: 13px;

  ${up('md')} {
    height: 38px;
    padding: 10px 15px;
    border: none;
    font-size: 14px;
  }

  .arrow-down-icon {
    width: 12px;

    svg {
      width: 12px;
    }
  }
`;

const UserFilterTitle = styled.span`
  color: ${({ theme }) => theme.colors.carbon};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  font-style: normal;
  line-height: 18px;
`;

const UserFilterSubTitle = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  line-height: 16px;
`;

const UserFilterRadiosContainer = styled(FlexColumn)`
  margin-top: 25px;

  & > * {
    &:not(:last-child) {
      margin-bottom: 28px !important;
    }
  }
`;

const ContextMenu = styled(FlexColumn)`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 6px;
  box-shadow: 0px 20px 40px 0px rgba(91, 101, 112, 0.1);
  padding: 20px 20px 15px;
  width: 296px;
`;

const CustomUserInputContainer = styled.div`
  padding: 6px 0px 0 30px;
`;

const OwnerFilterSubtitleContainer = styled(FlexRow)`
  align-items: center;
`;

const OwnerFilterInfoIcon = styled(Icon)`
  align-items: center;
  background: rgba(218, 224, 231, 1);
  border-radius: 50%;
  display: flex;
  height: 12px;
  justify-content: center;
  margin-left: 5px;
  width: 12px;

  svg {
    path {
      fill: ${({ theme }) => theme.colors.smalt};
    }
  }
`;

const UserFilterFormContentContainer = styled.div`
  padding: 0 20px;

  ${up('md')} {
    padding: 0;
  }
`;

const FilterTitlesContainer = styled.div`
  padding: 20px 20px 0px;

  ${up('md')} {
    padding: 0;
  }
`;

const MobileFilterButtonLabel = styled.span`
  margin: 0 5px;
`;

export enum DashboardUserFilterRadioValue {
  CUSTOM = 'CUSTOM',
  EVERYONE = 'EVERYONE',
  ONLY_ME = 'ONLY_ME',
}

export const dashboardUserFiltersFormSchema = z
  .object({
    owner: z.union([
      z.literal(DashboardUserFilterRadioValue.CUSTOM),
      z.literal(DashboardUserFilterRadioValue.EVERYONE),
      z.literal(DashboardUserFilterRadioValue.ONLY_ME),
    ]),
    targetUser: z.number().optional(),
  })
  .refine(
    ({ owner, targetUser }) =>
      !(owner === DashboardUserFilterRadioValue.CUSTOM && targetUser === undefined),
    { path: ['targetUser'] },
  );

export type DashboardUserFilterFormValues = z.infer<
  typeof dashboardUserFiltersFormSchema
>;

const dashboardFiltersDefaultValues: DashboardUserFilterFormValues = {
  owner: DashboardUserFilterRadioValue.EVERYONE,
  targetUser: undefined,
};

export const DashboardUserFilter = () => {
  const { t } = useTranslation(['common', 'dashboard']);
  const breakpointUpMd = useBreakpoint(up('md'));
  const userFilterVisibilityHandler = useVisible();
  const currentUser = useCurrentUser();

  const { setTargetUserId, targetUserId } = useContext(DashboardFiltersContext);

  const mainFiltersForm = useForm<DashboardUserFilterFormValues>({
    resolver: zodResolver(dashboardUserFiltersFormSchema),
    defaultValues: {
      owner: DashboardUserFilterRadioValue.EVERYONE,
    },
  });

  useEffect(() => {
    const watcher = mainFiltersForm.watch(({ owner, targetUser }) => {
      if (!owner) {
        return;
      }

      // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
      const selectedTargetUserId = match(owner)
        .with(DashboardUserFilterRadioValue.EVERYONE, () => undefined)
        .with(DashboardUserFilterRadioValue.ONLY_ME, () => currentUser.id)
        .with(DashboardUserFilterRadioValue.CUSTOM, () => targetUser)
        .exhaustive();

      setTargetUserId(selectedTargetUserId);
    });

    return () => watcher.unsubscribe();
  }, [currentUser, mainFiltersForm, setTargetUserId]);

  const innerForm = useForm<DashboardUserFilterFormValues>({
    resolver: zodResolver(dashboardUserFiltersFormSchema),
    defaultValues: mainFiltersForm.getValues(),
  });

  const { setFormFieldValue } = useSetFormFieldValue<DashboardUserFilterFormValues>({
    defaultValues: mainFiltersForm.getValues(),
    setValue: innerForm.setValue,
  });

  const getFilterOptionLabel = useCallback(
    (option: DashboardUserFilterRadioValue) => {
      // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
      return match(option)
        .with(DashboardUserFilterRadioValue.ONLY_ME, () => t('common:onlyMe'))
        .with(DashboardUserFilterRadioValue.EVERYONE, () => t('common:everyone'))
        .with(DashboardUserFilterRadioValue.CUSTOM, () => t('common:custom'))
        .exhaustive();
    },
    [t],
  );

  const getPrefixContent = useCallback(
    (option: DashboardUserFilterRadioValue) =>
      match(option)
        .with(DashboardUserFilterRadioValue.ONLY_ME, () => (
          <OnlyMeAvatar user={currentUser} />
        ))
        .with(DashboardUserFilterRadioValue.EVERYONE, () => (
          <UnassignedPrefixIcon type="everyone" />
        ))
        .with(DashboardUserFilterRadioValue.CUSTOM, () => (
          <StyledStackedCompanyUserList
            selectedUsersIds={targetUserId ? [targetUserId] : []}
            maxItems={2}
          />
        ))
        .otherwise(() => null),
    [currentUser, targetUserId],
  );

  const userFilterDropdown = useMemo(
    () =>
      breakpointUpMd ? (
        <UserFilterDropdown
          size="regular"
          variant="defaultWhite"
          icon="everyone"
          data-lgg-id="user-filter-dropdown-trigger"
          isActive={userFilterVisibilityHandler.visible}
          onClick={userFilterVisibilityHandler.show}
        >
          {getFilterOptionLabel(mainFiltersForm.getValues().owner)}
        </UserFilterDropdown>
      ) : (
        <MobileDropdownButton
          size="regular"
          variant="defaultWhite"
          isActive={userFilterVisibilityHandler.visible}
          onClick={userFilterVisibilityHandler.show}
          $isActive={
            mainFiltersForm.getValues().owner !== DashboardUserFilterRadioValue.EVERYONE
          }
        >
          {getPrefixContent(mainFiltersForm.getValues().owner)}
          <MobileFilterButtonLabel>{t('common:assignee')}</MobileFilterButtonLabel>
        </MobileDropdownButton>
      ),
    [
      breakpointUpMd,
      getFilterOptionLabel,
      getPrefixContent,
      mainFiltersForm,
      t,
      userFilterVisibilityHandler.show,
      userFilterVisibilityHandler.visible,
    ],
  );

  const clearTargetUserFieldState = useCallback(() => {
    innerForm.reset({
      ...dashboardFiltersDefaultValues,
      owner: innerForm.getValues().owner,
    });
  }, [innerForm]);

  const userFilterFormContent = useMemo(() => {
    return (
      <Controller
        control={innerForm.control}
        name="owner"
        defaultValue={DashboardUserFilterRadioValue.EVERYONE}
        render={({ field: ownerField }) => {
          return (
            <UserFilterRadiosContainer>
              <Radio
                value={DashboardUserFilterRadioValue.EVERYONE}
                text={getFilterOptionLabel(DashboardUserFilterRadioValue.EVERYONE)}
                testId="dashboard-user-filter-radio-option-everyone"
                name="dashboard-user-filter-radio-option-everyone"
                onChange={() => {
                  setFormFieldValue('owner', DashboardUserFilterRadioValue.EVERYONE);
                  clearTargetUserFieldState();
                }}
                checked={ownerField.value === DashboardUserFilterRadioValue.EVERYONE}
              />
              <Radio
                value={DashboardUserFilterRadioValue.ONLY_ME}
                text={getFilterOptionLabel(DashboardUserFilterRadioValue.ONLY_ME)}
                testId="dashboard-user-filter-radio-option-only-me"
                name="dashboard-user-filter-radio-option-only-me"
                onChange={() => {
                  setFormFieldValue('owner', DashboardUserFilterRadioValue.ONLY_ME);
                  clearTargetUserFieldState();
                }}
                checked={ownerField.value === DashboardUserFilterRadioValue.ONLY_ME}
              />
              <span>
                <Radio
                  value={DashboardUserFilterRadioValue.CUSTOM}
                  text={`${getFilterOptionLabel(DashboardUserFilterRadioValue.CUSTOM)}:`}
                  testId="dashboard-user-filter-radio-option-custom"
                  name="dashboard-user-filter-radio-option-custom"
                  onChange={() => {
                    setFormFieldValue('owner', DashboardUserFilterRadioValue.CUSTOM);
                  }}
                  checked={ownerField.value === DashboardUserFilterRadioValue.CUSTOM}
                />
                <CustomUserInputContainer>
                  <Controller
                    control={innerForm.control}
                    name="targetUser"
                    render={({ field, fieldState }) => {
                      return (
                        <CompanyUsersSelect
                          name="dashboard-user-filter-user-select"
                          data-lgg-id="dashboard-user-filter-user-select"
                          label=""
                          isDisabled={
                            ownerField.value !== DashboardUserFilterRadioValue.CUSTOM
                          }
                          hasError={Boolean(fieldState.error)}
                          selectedUsersIds={field.value ? [field.value] : []}
                          onChange={(option) => {
                            innerForm.clearErrors('targetUser');

                            if (!option) {
                              innerForm.setValue('targetUser', undefined);
                              return;
                            }

                            setFormFieldValue(field.name, option.value);
                          }}
                          isMulti={false}
                        />
                      );
                    }}
                  />
                </CustomUserInputContainer>
              </span>
            </UserFilterRadiosContainer>
          );
        }}
      />
    );
  }, [clearTargetUserFieldState, innerForm, getFilterOptionLabel, setFormFieldValue]);

  const handleFormSubmit = useCallback(() => {
    return innerForm.handleSubmit(({ owner, targetUser }) => {
      mainFiltersForm.setValue('owner', owner);
      mainFiltersForm.setValue('targetUser', targetUser);

      userFilterVisibilityHandler.close();
    })();
  }, [innerForm, mainFiltersForm, userFilterVisibilityHandler]);

  const filterActionButtons = useMemo(
    () => (
      <OverlayFilterActionButtons
        onReset={() => {
          innerForm.reset(dashboardFiltersDefaultValues);
          mainFiltersForm.reset(dashboardFiltersDefaultValues);

          userFilterVisibilityHandler.close();
        }}
        onSubmit={async (e) => {
          preventDefaultAndStopPropagation(e);

          await handleFormSubmit();
        }}
        baseTestId="dashboard-user-filter"
      />
    ),
    [innerForm, handleFormSubmit, mainFiltersForm, userFilterVisibilityHandler],
  );

  const userFormContent = useMemo(
    () => (
      <>
        <FilterTitlesContainer>
          <UserFilterTitle>{t('dashboard:filters.ownerFilter.title')}</UserFilterTitle>
          <OwnerFilterSubtitleContainer>
            <UserFilterSubTitle>
              {t('dashboard:filters.ownerFilter.subtitle')}
            </UserFilterSubTitle>
            <OwnerFilterInfoIcon type="info" />
          </OwnerFilterSubtitleContainer>
        </FilterTitlesContainer>
        <form>
          <UserFilterFormContentContainer>
            {userFilterFormContent}
          </UserFilterFormContentContainer>
        </form>
      </>
    ),
    [t, userFilterFormContent],
  );

  return (
    <span>
      {breakpointUpMd ? (
        <LggPopover
          visible={userFilterVisibilityHandler.visible}
          content={
            <ContextMenu data-lgg-id="user-filter-overlay">
              <>
                {userFormContent}
                {filterActionButtons}
              </>
            </ContextMenu>
          }
          trigger={['click']}
          overlayStyle={{
            padding: 0,
          }}
          hideArrow
          onVisibleChange={(isVisible) => {
            userFilterVisibilityHandler.setVisible(isVisible);

            if (!isVisible) {
              innerForm.reset(mainFiltersForm.getValues());
            }
          }}
          placement="bottomRight"
        >
          <span>{userFilterDropdown}</span>
        </LggPopover>
      ) : (
        <>
          {userFilterDropdown}
          <BottomDrawer
            visible={userFilterVisibilityHandler.visible}
            title={t('common:filters.title')}
            onClose={() => {
              userFilterVisibilityHandler.close();
              innerForm.reset(mainFiltersForm.getValues());
            }}
            fullHeight
            footer={filterActionButtons}
          >
            {userFormContent}
          </BottomDrawer>
        </>
      )}
    </span>
  );
};
